import React, { createContext, ReactNode, useCallback, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import mixpanel from '../../config/analytics/mixpanel';
import sentry from '../../config/analytics/sentry';
import mouseflowUtil from '../../utils/mouseflow';
import {
  identifyUserData,
  UserData,
} from '../../config/analytics/identifyUser';
import { USER_ID, USER_TYPE } from '../../utils/constants';

interface AnalyticsConfig {
  mixpanel: typeof mixpanel;
  sentry: typeof sentry;
  mouseflow: typeof mouseflowUtil;
  identifyUser: (userData: UserData) => void;
}

// eslint-disable-next-line @typescript-eslint/no-empty-function
const defaultIdentifyUser = () => {};

const analyticsConfig: AnalyticsConfig = {
  mixpanel,
  sentry,
  mouseflow: mouseflowUtil,
  identifyUser: defaultIdentifyUser,
};

export const AnalyticsContext = createContext<AnalyticsConfig>(analyticsConfig);

const ContextProvider = AnalyticsContext.Provider;

const AnalyticsContextProvider = ({ children }: { children?: ReactNode }) => {
  const [cookies] = useCookies([USER_ID, USER_TYPE]);
  const userId = cookies[USER_ID];
  const userType = cookies[USER_TYPE];

  const identifyUser = useCallback((userData: UserData) => {
    identifyUserData(userData);
  }, []);

  useEffect(() => {
    if (userId) {
      identifyUser({ id: userId, type: userType });
    }
  }, [userId, userType, identifyUser]);

  const contextValue = React.useMemo(
    () => ({
      mixpanel,
      sentry,
      mouseflow: mouseflowUtil,
      identifyUser,
    }),
    [identifyUser]
  );

  return <ContextProvider value={contextValue}>{children}</ContextProvider>;
};

export default AnalyticsContextProvider;
